import { useMemo } from 'react'

interface TransitionSuggestion {
  student_id: number
  student_name: string
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  to_unit_type: string
  to_unit_id: number
  to_unit_name: string
  current_membership_id: number
}

interface RepeaterCandidateStudent {
  student_id: number
  student_name: string
  current_membership_id: number
}

interface RepeaterCandidateGroup {
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  students: RepeaterCandidateStudent[]
}

interface TransitionGroup {
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  to_unit_type: string
  to_unit_id: number
  to_unit_name: string
  transitions: TransitionSuggestion[]
  repeater_count: number
}

interface Props {
  groups: TransitionGroup[]
  repeaterCandidates: RepeaterCandidateGroup[]
  selectedTransitionIds: Set<number>
  selectedRepeaterIds: Set<number>
  onToggleTransition: (membershipId: number) => void
  onToggleRepeaterGroup: (group: RepeaterCandidateGroup) => void
  onToggleRepeater: (membershipId: number) => void
  onSelectAllTransitions: () => void
  onDeselectAllTransitions: () => void
}

export default function TransitionPreviewAccordion({
  groups,
  repeaterCandidates,
  selectedTransitionIds,
  selectedRepeaterIds,
  onToggleTransition,
  onToggleRepeaterGroup,
  onToggleRepeater,
  onSelectAllTransitions,
  onDeselectAllTransitions
}: Props) {
  const groupsWithTransitions = useMemo(
    () => groups.filter(g => g.transitions.length > 0),
    [groups]
  )
  const repeaterGroups = useMemo(
    () => repeaterCandidates.filter(g => g.students.length > 0),
    [repeaterCandidates]
  )

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
        Hier wird noch nichts geändert. Bitte die Zuordnungen prüfen.
      </div>

      <div className="flex gap-3 text-sm">
        <button type="button" onClick={onSelectAllTransitions} className="text-primary-600 hover:underline">
          Alle Übergänge auswählen
        </button>
        <button type="button" onClick={onDeselectAllTransitions} className="text-primary-600 hover:underline">
          Alle Übergänge abwählen
        </button>
      </div>

      <div className="space-y-3">
        {groupsWithTransitions.map((group) => {
          const selectedInGroup = group.transitions.filter(t => selectedTransitionIds.has(t.current_membership_id)).length
          return (
            <details key={`${group.from_unit_type}-${group.from_unit_id}-${group.to_unit_id}`} className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900" open>
              <summary className="cursor-pointer list-none px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-50">
                    {group.from_unit_name} → {group.to_unit_name}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {selectedInGroup} von {group.transitions.length} ausgewählt
                  </span>
                </div>
              </summary>
              <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
                <ul className="space-y-1">
                  {group.transitions.map((transition, index) => (
                    <li key={transition.current_membership_id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={selectedTransitionIds.has(transition.current_membership_id)}
                        onChange={() => onToggleTransition(transition.current_membership_id)}
                        className="rounded border-gray-300"
                      />
                      <span>{transition.student_name}</span>
                      {index === 4 && group.transitions.length > 5 && (
                        <span className="text-xs text-gray-500">+{group.transitions.length - 5} weitere</span>
                      )}
                    </li>
                  )).slice(0, 5)}
                </ul>
              </div>
            </details>
          )
        })}
      </div>

      {repeaterGroups.length > 0 && (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
          <h4 className="text-sm font-medium text-amber-900 dark:text-amber-200">Bleibt in der gleichen Klasse / im gleichen Kurs</h4>
          {repeaterGroups.map((group) => {
            const allSelected = group.students.every(st => selectedRepeaterIds.has(st.current_membership_id))
            return (
              <div key={`${group.from_unit_type}-${group.from_unit_id}`} className="rounded border border-amber-200 bg-white p-3 dark:border-amber-700 dark:bg-gray-900">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{group.from_unit_name}</p>
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => onToggleRepeaterGroup(group)}
                      className="rounded border-gray-300"
                    />
                    Ganze Einheit markieren
                  </label>
                </div>
                <ul className="space-y-1">
                  {group.students.map(student => (
                    <li key={student.current_membership_id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={selectedRepeaterIds.has(student.current_membership_id)}
                        onChange={() => onToggleRepeater(student.current_membership_id)}
                        className="rounded border-gray-300"
                      />
                      <span>{student.student_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
